import { useState } from "react";
import { useAuth } from "../context/authContext";
import Header from "../components/Header";
import "./RequestPasswordReset.css";

const RequestPasswordReset = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error("Please enter a valid email address");
      }

      const result = await requestPasswordReset(email);

      if (result?.status === true) {
        setSuccess(true); // ✅ show success state
      } else {
        setError(result?.message || "Password reset failed. Please try again.");
      }
    } catch (err) {
      setError(
        err.message ||
          "Failed to send reset email. Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Success State ── */
  if (success) {
    return (
      <div className="request-password-page">
        <div className="request-container">
          <Header />
          <div className="request-password-card">
            <div className="reset-password-header">
              <div className="reset-password-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h1 className="reset-password-title">Check Your Email</h1>
              <p className="reset-password-subtitle">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
            </div>

            <div className="reset-password-body">
              <div className="success-alert">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <div>
                  <strong>Email sent successfully!</strong>
                  <p>
                    Please check your inbox (and spam folder) for a password
                    reset link. The link expires in 10 minutes.
                  </p>
                </div>
              </div>

              <div className="resend-section">
                <p className="resend-text">Didn't receive the email?</p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail("");
                  }}
                  className="submit-button resend-button"
                >
                  Resend Email
                </button>
              </div>
            </div>

            <div className="reset-password-footer">
              <a href="/login" className="footer-link">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form State ── */
  return (
    <div className="request-password-page">
      <div className="request-container">
        <Header />
        <div className="request-password-card">
          {/* Header */}
          <div className="reset-password-header">
            <div className="reset-password-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <h1 className="reset-password-title">Forgot Password?</h1>
            <p className="reset-password-subtitle">
              Enter your email address and we'll send you a link to reset your
              password
            </p>
          </div>

          {/* Body */}
          <div className="reset-password-body">
            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* Error Alert */}
              {error && (
                <div className="error-alert">
                  <div className="error-alert-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="error-alert-content">
                    <p className="error-alert-title">Error</p>
                    <p className="error-alert-message">{error}</p>
                  </div>
                </div>
              )}

              {/* Email Input */}
              <div className="form-group">
                <label htmlFor="email" className="form-label required">
                  Email Address
                </label>
                <div className="password-input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="Enter your email address"
                    required
                    autoFocus
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#C9A84C",
                      pointerEvents: "none",
                    }}
                  >
                    <svg
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                </div>
                <p className="help-text">
                  We'll send a password reset link to this email
                </p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !email}
                className={`submit-button ${loading ? "loading" : ""}`}
              >
                {loading && <span className="button-spinner"></span>}
                {loading ? "Sending Email..." : "Send Reset Link"}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="reset-password-footer">
            <a href="/login" className="footer-link">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestPasswordReset;
